/**
 * useSecurityValidator
 *
 * Hook responsável por validar o estado de autenticação (tokens + userInfo).
 * Gerencia os timeouts e transições de estado do SecurityGate.
 *
 * Extração: Fase 3 - Refatoração ToolsMenu.tsx
 * @author Simplificação 2026-01-13
 */

import { useState, useEffect } from 'react';
import { TokenService } from '@/lib/tokenService';
import { sendToTerminal } from '@/lib/terminalLogger';

export type AuthState = 'checking' | 'valid' | 'invalid';

export interface SecurityValidatorResult {
  /** Estado atual da autenticação */
  authState: AuthState;
  /** Setter para atualizar o estado (usado por outros hooks) */
  setAuthState: (state: AuthState) => void;
}

/**
 * Hook para validar autenticação com timeouts adaptativos
 *
 * Fluxo:
 * 1. Inicia como 'checking'
 * 2. Verifica token + userInfo via polling (50ms)
 * 3. Escuta evento 'auth:updated'
 * 4. Timeout L1 (1.5s): Warning se não tiver token
 * 5. Timeout L2 (3s): Marca valid OU invalid
 */
export function useSecurityValidator(userInfo: any): SecurityValidatorResult {
  const [authState, setAuthState] = useState<AuthState>('checking');

  // Validação inicial com timeouts
  useEffect(() => {
    const checkAuthState = (): { hasToken: boolean; hasUserInfoNow: boolean } => {
      const accessToken = TokenService.getAccessToken();
      return {
        hasToken: !!accessToken,
        hasUserInfoNow: !!userInfo
      };
    };

    const handleAuthUpdated = () => {
      const { hasToken: t, hasUserInfoNow: u } = checkAuthState();
      if (t && u) {
        setAuthState('valid');
      }
    };

    window.addEventListener('auth:updated', handleAuthUpdated as any);

    // Polling rápido para verificar (50ms)
    const intervalId = setInterval(() => {
      const { hasToken: t, hasUserInfoNow: u } = checkAuthState();
      if (t && u) {
        setAuthState('valid');
        clearInterval(intervalId);
      }
    }, 50);

    // Timeout L1: 1.5s - warning se não tiver token
    const timeoutL1 = setTimeout(() => {
      const { hasToken: t } = checkAuthState();
      if (!t) {
        sendToTerminal('⚠️ [SECURITY] Token não encontrado após 1.5s', {});
      }
    }, 1500);

    // Timeout L2: 3s - decisão final
    const timeoutL2 = setTimeout(() => {
      clearInterval(intervalId);
      const { hasToken: t, hasUserInfoNow: u } = checkAuthState();

      if (t && u) {
        setAuthState('valid');
      } else if (t) {
        // Tem token mas sem userInfo - ainda válido (userInfo pode vir do bootstrap)
        setAuthState('valid');
        sendToTerminal('⚠️ [SECURITY] Token OK, userInfo pendente (continuando)', {});
      } else {
        setAuthState('invalid');
        sendToTerminal('❌ [SECURITY] Tokens não validados após timeout', {
          hasToken: t,
          hasUserInfo: u
        });
      }
    }, 3000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutL1);
      clearTimeout(timeoutL2);
      window.removeEventListener('auth:updated', handleAuthUpdated as any);
    };
  }, []); // Executa apenas no mount

  // Atualizar estado quando userInfo ou token mudarem (após mount)
  useEffect(() => {
    if (authState === 'checking' || authState === 'invalid') {
      const accessToken = TokenService.getAccessToken();
      if (accessToken && userInfo) {
        setAuthState('valid');
      }
    }
  }, [userInfo, authState]);

  return { authState, setAuthState };
}
